import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, from } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom, debounceTime, mergeMap, filter } from 'rxjs/operators';
import * as InProgressDashboardActions from './in-progress-dashboard.actions';
import { selectDashboardFilters, selectIsInitialized } from './in-progress-dashboard.selectors';
import { DashboardFilters } from './in-progress-dashboard.state';
import { DashboardService } from '../../services/dashboard.service';

const STORAGE_KEY = 'inProgressDashboardFilters';

@Injectable()
export class InProgressDashboardEffects {
  private actions$ = inject(Actions);
  private dashboardService = inject(DashboardService);
  private store = inject(Store);

  constructor() { }

  // Remove user teams loading as teams now come from current-user store
  // loadUserTeams$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(InProgressDashboardActions.loadUserTeams),
  //     switchMap(() =>
  //       this.dashboardService.getUserTeams().pipe(
  //         map(teams => InProgressDashboardActions.loadUserTeamsSuccess({ teams })),
  //         catchError(error => of(InProgressDashboardActions.loadUserTeamsFailure({ 
  //           error: error.message || 'Failed to load user teams' 
  //         })))
  //       )
  //     )
  //   )
  // );

  loadDashboardData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InProgressDashboardActions.loadDashboardData),
      withLatestFrom(this.store.select(selectDashboardFilters)),
      switchMap(([_action, filters]) => {
        return this.dashboardService.getInProgressDashboardData(
          filters.teamIds,
          filters.myAssignmentsOnly,
          filters.searchText
        ).pipe(
          map(response => InProgressDashboardActions.loadDashboardDataSuccess({ 
            userTeams: response.userTeams,
            columns: response.columns 
          })),
          catchError(error => of(InProgressDashboardActions.loadDashboardDataFailure({ 
            error: error.message || 'Failed to load dashboard data' 
          })))
        );
      })
    )
  );

  reloadOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        InProgressDashboardActions.setTeamFilter,
        InProgressDashboardActions.setMyAssignmentsFilter,
        InProgressDashboardActions.setSearchTextFilter,
        InProgressDashboardActions.updateFilters
      ),
      withLatestFrom(this.store.select(selectIsInitialized)),
      filter(([_action, isInitialized]) => {
        // Only reload if dashboard is already initialized
        // This prevents double load during initialization
        return isInitialized;
      }),
      debounceTime(300),
      map(() => InProgressDashboardActions.loadDashboardData())
    )
  );

  persistFiltersToStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        InProgressDashboardActions.setTeamFilter,
        InProgressDashboardActions.setMyAssignmentsFilter,
        InProgressDashboardActions.setSearchTextFilter,
        InProgressDashboardActions.updateFilters,
        InProgressDashboardActions.clearFilters
      ),
      withLatestFrom(this.store.select(selectDashboardFilters)),
      map(([_action, filters]) => {
        this.saveFiltersToStorage(filters);
        return { type: 'NO_ACTION' };
      })
    ),
    { dispatch: false }
  );

  initializeDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InProgressDashboardActions.initializeDashboard),
      switchMap(() => {
        const actions: any[] = [];
        
        // Hydrate filters from storage first
        try {
          const storedFilters = localStorage.getItem(STORAGE_KEY);
          if (storedFilters) {
            // Team selection is owned by DashboardTeamSelectionStore (shared with
            // Resolved), so a legacy teamIds entry here must not override it.
            const { teamIds: _teamIds, ...filters }: Partial<DashboardFilters> = JSON.parse(storedFilters);
            actions.push(InProgressDashboardActions.hydrateFiltersFromStorage({ filters }));
          }
        } catch (error) {
          console.warn('Failed to hydrate dashboard filters from storage:', error);
        }
        
        // Add data loading action
        // Don't load teams here - they come from current-user store
        actions.push(InProgressDashboardActions.loadDashboardData());
        
        return from(actions);
      }),
      mergeMap(action => of(action))
    )
  );

  private saveFiltersToStorage({ teamIds: _teamIds, ...filters }: DashboardFilters): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.warn('Failed to save dashboard filters to storage:', error);
    }
  }
}